import { SurfError, SurfErrorCode } from "../utils/errors";

export interface HttpClientOptions {
  baseUrl: string;
  projectName: string;
  projectId: string;
  appId: string;
}

export class HttpClient {
  constructor(private readonly options: HttpClientOptions) {}

  getAppId(): string {
    return this.options.appId;
  }

  async get<T>(path: string, headers?: HeadersInit): Promise<T> {
    const init: RequestInit = { method: "GET" };
    if (headers !== undefined) {
      init.headers = headers;
    }
    return this.request<T>(path, init);
  }

  async post<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
    const init: RequestInit = {
      method: "POST",
    };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }
    if (headers !== undefined) {
      init.headers = headers;
    }
    return this.request<T>(path, init);
  }

  private async request<T>(
    path: string,
    init: RequestInit,
  ): Promise<T> {
    const response = await fetch(this.resolveUrl(path), {
      ...init,
      // Auth is cookie-based: the backend sets an httpOnly session cookie
      // (SameSite=None) on login. Including credentials makes the browser send
      // and receive that cookie cross-origin on every request.
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Surf-Project-Name": this.options.projectName,
        "X-Surf-Project-Id": this.options.projectId,
        ...(init.headers ?? {}),
      },
    });

    if (!response.ok) {
      throw new SurfError(
        SurfErrorCode.API_ERROR,
        `Request failed with status ${response.status}`,
        await response.text().catch(() => undefined),
      );
    }

    return (await response.json()) as T;
  }

  private resolveUrl(path: string): string {
    const baseUrl = this.options.baseUrl.replace(/\/$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    if (baseUrl.endsWith("/api") && normalizedPath.startsWith("/api/")) {
      return `${baseUrl}${normalizedPath.slice(4)}`;
    }
    return `${baseUrl}${normalizedPath}`;
  }
}
